// src/web/Acknowledgements.jsx

import { useTranslation, Trans } from 'react-i18next';
import PageTracker from '../../components/tracking/PageTracker';
import ScrollTracker from '../../components/tracking/ScrollTracker';

function Acknowledgements() {
  const { t } = useTranslation();

  return (
    <div className="acknowledgements-page">
      <PageTracker pageName="Acknowledgements" />

      {/* 🏆 HERO */}
      <section className="ack-hero">
        <div className="ack-hero-content">
          <h1>{t('ack_title', 'Acknowledgements')}</h1>

          <p>
            <Trans i18nKey="ack_intro">
              Recognition is not the goal — it’s the echo of shared effort, curiosity, and collaboration.
              Each mention, award, and citation represents a collective commitment to advancing the understanding of the human voice.
            </Trans>
          </p>
        </div>
      </section>

      {/* 🏅 AWARDS */}
      <section className="ack-section">
        <h2>{t('ack_awards_title', 'Awards & Recognitions')}</h2>

        <ul className="ack-cards">

          {/* Santander X */}
          <li className="ack-card">
            <img
              src="https://www.uoc.edu/content/dam/news/images/noticies/2023/182-impulsar-emprenedoria.jpg"
              alt="IEEE SPS Entrepreneurship Forum ICASSP 2026"
              className="ack-card-image"
            />
            <h3>
              <a
                href="https://www.santanderx.com/en/index.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                Santander X Spain Awards | University 2026 ↗
              </a>
            </h3>

            <p>
              <strong>Awarded as winning project in the UOC selection (2026)</strong>
            <br />
            Selected as one of the three winning university entrepreneurial projects in the Universitat Oberta de Catalunya's Santander X Spain Awards 2026 call.
            <br />
            Direct access to compete in the <strong>Santander X Spain Award | University 2026</strong> national stage.
            <br />
            <span className="award-giver">Universitat Oberta de Catalunya · Santander X</span>
            </p>
          </li>

          {/* IEEE */}
          <li className="ack-card">
            <img
              src="/images/acknowledgements/sps_entrepreneurship_forum.png"
              alt="IEEE SPS Entrepreneurship Forum ICASSP 2026"
              className="ack-card-image"
            />
            <p className="ack-image-credit">
              Photo: La Salle Technova Barcelona
            </p>

            <h3>
              <a
                href="https://2026.ieeeicassp.org/sps-entrepreneurship-forum/"
                target="_blank"
                rel="noopener noreferrer"
              >
                IEEE SPS Entrepreneurship Forum (ICASSP 2026) ↗
              </a>
            </h3>

            <p>
              <strong>Selected as Finalist (2026)</strong>
              <br />
              Startup Pitch Competition (6 selected out of 25 projects).
              <br />
              <span className="award-giver">IEEE Signal Processing Society</span>
            </p>

            {/*<div className="ack-links">
              <a href="/insights/icassp-pitch">Insights →</a>
            </div>*/}
          </li>

          {/* SpinUOC */}
          <li className="ack-card">
            <img
              src="/images/acknowledgements/spinuoc.jpg"
              alt="SpinUOC Awards 2025"
              className="ack-card-image"
            />

            <h3>
              <a
                href="https://www.uoc.edu/en/news/2025/com-transformar-la-avaluacio-clinica-de-la-veu"
                target="_blank"
                rel="noopener noreferrer"
              >
                SpinUOC 2025 Awards ↗
              </a>
            </h3>

            <p>
              <strong>Awarded two prizes (2025):</strong>
              <br />
              – Ramon Molinas Foundation Social Impact Award
              <br />
              – Audience Award for Best Pitch Presentation
              <br />
              <span className="award-giver">Universitat Oberta de Catalunya</span>
            </p>
          </li>

          {/* Hamdan */}
          <li className="ack-card">
            <img
              src="/images/acknowledgements/hamdan.jpg"
              alt="Hamdan International Presenter Award"
              className="ack-card-image"
            />

            <h3>
              <a
                href="https://voicefoundation.org/annual-symposium/awards-recognizing-excellence/hamdan-international-presenter-award/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Hamdan International Presenter Award 2025 ↗
              </a>
            </h3>

            <p>
              <strong>Awarded to Neus Calaf, PhD (2025)</strong>
              <br />
              For presenting research developed through <strong>All-Voiced</strong>.
              <br />
              <span className="award-giver">The Voice Foundation Symposium</span>
            </p>
          </li>

          {/* Creatic */}
          <li className="ack-card">
            <img
              src="/images/acknowledgements/creatic.jpg"
              alt="Creatic Awards 2025"
              className="ack-card-image"
            />

            <h3>
              <a
                href="https://www.tecnocampus.cat/en/premis-creatic/presentacio-premis-creatic"
                target="_blank"
                rel="noopener noreferrer"
              >
                Creatic Awards 2025 ↗
              </a>
            </h3>

            <p>
              <strong>Selected as Finalist (2025)</strong>
              <br />
              One of 12 finalist projects in a competitive program recognizing
              innovative and technology-based entrepreneurial initiatives.
              <br />
              <span className="award-giver">TecnoCampus & Ajuntament de Mataró</span>
            </p>
          </li>

        </ul>
      </section>

      {/* 🚀 PROGRAMS & FELLOWSHIPS */}
      <section className="ack-section">
        <h2>{t('ack_ecosystems_title', 'Entrepreneurship & Innovation Ecosystems')}</h2>

        <ul className="ack-cards">

        {/* INNOVATIA */}
        <li className="ack-card">
          <h3>
            <a
              href="https://innovatia83.es/"
              target="_blank"
              rel="noopener noreferrer"
            >
              INNOVATIA 8.3 — Women-led Scientific Entrepreneurship ↗
            </a>
          </h3>

          <p>
            Participated in the <strong>2026 edition</strong> of INNOVATIA 8.3, a national program supporting innovative women-led companies and entrepreneurial initiatives created with the support of universities and public research organizations.
            <br />
            <span className="award-giver">
              Instituto de las Mujeres · Universidade de Santiago de Compostela · Proyecto Innovatia 8.3
            </span>
          </p>
        </li>

        </ul>
      </section>

      {/* 🚀 PROGRAMS & FELLOWSHIPS */}
      <section className="ack-section">
        <h2>{t('ack_programs_title', 'Programs & Fellowships')}</h2>

        <ul className="ack-cards">

          {/* HUBBIK */}
          <li className="ack-card">

            <h3>
              <a
                href="https://www.uoc.edu/en/research/entrepreneurship"
                target="_blank"
                rel="noopener noreferrer"
              >
                Hubbik — UOC Entrepreneurship & Innovation Ecosystem ↗
              </a>
            </h3>

            <p>
              Participant in <strong>Hubbik</strong>, the Universitat Oberta de Catalunya’s entrepreneurship and innovation ecosystem supporting knowledge transfer and impact-driven initiatives.
              <br />
              <span className="award-giver">Universitat Oberta de Catalunya (UOC)</span>
            </p>
          </li>

          {/* AI4ALL */}
          <li className="ack-card">

            <h3>
              <a
                href="https://www.ai4all.cat/"
                target="_blank"
                rel="noopener noreferrer"
              >
                AI4ALL — Artificial Intelligence Applied to Industry ↗
              </a>
            </h3>

            <p>
              Completed the <strong>2025 edition</strong>, a specialized program focused on AI-driven innovation and technology transfer.
              <br />
              <span className="award-giver">UAB Research Park · CVC · IIIA-CSIC</span>
            </p>
          </li>

          {/* APTenisa */}
          <li className="ack-card">

            <h3>
              <a
                href="https://www.apte.org/aptenisa/"
                target="_blank"
                rel="noopener noreferrer"
              >
                APTenisa — Startup Ideation & Acceleration Program ↗
              </a>
            </h3>

            <p>
              Selected to participate in the <strong>2025 edition</strong> of the APTenisa Launch acceleration program led by La Salle Technova Barcelona, focused on technology-based entrepreneurship and startup development.
              <br />
              <span className="award-giver">APTE · ENISA</span>
            </p>
          </li>

        </ul>
      </section>
      
      {/* 📖 CITATIONS */}
      <section className="ack-section">
        <h2>{t('ack_citations_title', 'Scientific Citations & Mentions')}</h2>
        <div className="ack-list">
            <p><strong>Behlau, M.</strong> (2024, September 5). <i>Voice and Culture - Broadening Horizons</i> [Conference presentation]. 15th Pan-European Voice Conference (PEVOC), Santander, Spain.</p>
            <p><strong>Kempster, G. B., Nagle, K. F., & Solomon, N. P.</strong> (2025). Development and rationale for the consensus auditory-perceptual evaluation of voice-revised (CAPE-Vr). <i>Journal of Voice.</i> <a href="https://doi.org/10.1016/j.jvoice.2025.01.022"target="_blank" rel="noopener noreferrer">https://doi.org/10.1016/j.jvoice.2025.01.022</a></p>
            <p><strong>Kempster, G., Nagle, K., & Solomon, N.</strong> (2025). A revised CAPE-V: Protocol & practice [In-person seminar]. ASHA Convention 2025.</p>
            <p><strong>Nagle, K.</strong> (2024, May 31). <i>Describing voice quality in 5 minutes: Balancing feasibility and psychometric validity</i> [Conference presentation]. 53rd Annual Symposium of the Voice Foundation, Philadelphia, PA, United States.</p>
            <p><strong>Nagle, K.</strong> (2025). The Voice is More Than The Sum of its Parts: Challenges to Perceptual Evaluation of Disordered Voice.<i> The Voice, 30</i>(1). The Voice Foundation Newsletter. Retrieved from <a href="https://indd.adobe.com/view/bc611740-7979-480b-8c25-2147c2ab8252" target="_blank" rel="noopener noreferrer">https://indd.adobe.com/view/bc611740-7979-480b-8c25-2147c2ab8252</a> </p>
            <p><strong>Nagle, K.</strong> (2025, March 28). <i>Voice Assessment for School-Based SLPs: The Basics</i> [Conference presentation]. 2025 New Jersey Speech-Language Hearing Association Annual Convention, Atlantic City, NJ, United States.</p>
            <p><strong>Nagle, K. F., Kempster, G. B., & Solomon, N. P.</strong> (2024). Survey of Voice-Focused Speech-Language Pathologists’ Usage of the Consensus Auditory Perceptual Evaluation of Voice (CAPE-V). <i>Journal of Voice.</i> <a href="https://doi.org/10.1016/j.jvoice.2024.08.032" target="_blank" rel="noopener noreferrer">https://doi.org/10.1016/j.jvoice.2024.08.032</a></p>
            <p><strong>Pommée, T., Defoy, L., & Verduyckt, I.</strong> (2025). From practice to protocol: The CAPE-VFQ by and for Quebec French SLPs. <i> Clinical Linguistics & Phonetics, 1-12.</i>. <a href="https://doi.org/10.1080/02699206.2025.2456214" target="_blank" rel="noopener noreferrer">https://doi.org/10.1080/02699206.2025.2456214</a></p>
            <p><strong>Pommée, T., Renaud, S. E., & Verduyckt, I.</strong> (2025). Reliability and Task Effects in CAPE-V Auditory-Perceptual Voice Assessments: Insights From the PVQD30 Subset <i> Journal of Voice.</i>. <a href="https://doi.org/10.1016/j.jvoice.2025.02.020" target="_blank" rel="noopener noreferrer">https://doi.org/10.1016/j.jvoice.2025.02.020</a></p>
            <p><strong>Pommée, T., Younes, S., Gauthier, L., & Verduyckt, I.</strong> (2025). Improving Auditory-Perceptual Voice Assessment: A Hybrid In-Class and Online CAPE-V Training Study. <i> Journal of Voice.</i> <a href="https://doi.org/10.1016/j.jvoice.2025.11.029" target="_blank" rel="noopener noreferrer">https://doi.org/10.1016/j.jvoice.2025.11.029</a></p>
            <p><strong>Shea, B., Palmer, A., Chang, S., Childes, J., & Graville, D. J.</strong> (2024, December 5–7). <i>Current practices in the perceptual assessment of voice in research</i> [Poster presentation]. 2024 American Speech, Language and Hearing Association (ASHA) Convention, Seattle, Washington, United States.</p>
            <p><strong>Verduyckt, I. & Renaud, S.E.</strong> (2024, September 6). <i>Reliability, Validity and Usability of the French Quebec Version of the CAPE-V: a Mixed Methods Study</i>. [Conference presentation]. 15th Pan-European Voice Conference (PEVOC), Santander, Spain.</p>
        </div>
      </section>

      {/* 🗣️ INVITED TALKS */}
      <section className="ack-section">
      <h2>{t('ack_talks_title', 'Invited Talks & Panels')}</h2>
      <div className="ack-list">
          <p>
          <strong>Calaf, N.</strong> (2025, April 5). <i>Hacia un consenso en la categorización de los atributos perceptuales para la caracterización de las voces artísticas</i> [Conference presentation]. 
          II Simposio Voz Artística y Salud Vocal, Facultad de Psicología, Ciencias de la Educación y del Deporte Blanquerna, Universitat Ramon Llull, Barcelona, Spain.
          </p>

          <p>
          <strong>Calaf, N.</strong> (2025, June 27). <i>Entrenamiento en evaluación auditivo-perceptiva de la voz con interacción y feedback en tiempo real</i> [Workshop]. 
          XXXIV Congreso Internacional de Logopedia, Foniatría y Audiología, Asociación Española de Logopedia, Foniatría y Audiología e Iberoamericana de Fonoaudiología (AELFA-IF), Salamanca, Spain.
          </p>
      </div>
      </section>

      <ScrollTracker pageName="Acknowledgements" />
    </div>
  );
}

export default Acknowledgements;